import { Stack } from "@mui/material";
import { TextButton } from "@src/appV2/redesign/components/TextButton";
import { List, ListItem, Text } from "@src/appV2/redesign/system/ui-components";

import { SegmentedText } from "./SegmentedText";
import { TermsListItem } from "./TermsListItem";
import { getCancellationProtectionDescription } from "./utils/getCancellationProtectionDescription";

interface ShiftTermsProps {
  variant: "shift";
  cancellationProtectionWindowInHours: number;
  isSentHomeProtected: boolean;
}

interface OverviewTermsProps {
  variant: "overview";
}

type CancellationProtectionTermsListProps = ShiftTermsProps | OverviewTermsProps;

const OVERVIEW_FIRST_ITEM_DESCRIPTION =
  "Most workplaces pay a partial amount when they cancel your shift or send you home early. This protection covers the difference so you're paid in full.";

export function CancellationProtectionTermsList(props: CancellationProtectionTermsListProps) {
  return (
    <Stack spacing={9}>
      <TermsListItem iconType="clock" title="Your time and income matter">
        {props.variant === "shift" ? (
          <SegmentedText
            segments={getCancellationProtectionDescription(
              props.cancellationProtectionWindowInHours,
              props.isSentHomeProtected
            )}
            sx={{ color: (theme) => theme.palette.text.secondary }}
          />
        ) : (
          OVERVIEW_FIRST_ITEM_DESCRIPTION
        )}
      </TermsListItem>

      <TermsListItem
        iconType="building"
        title={
          props.variant === "shift"
            ? "Only applies when workplace cancels"
            : "Protection only applies when workplace cancels or sends you home"
        }
      >
        <Text variant="body2" sx={{ color: (theme) => theme.palette.text.secondary }}>
          This protection doesn&apos;t apply in cases including, but not limited to:
        </Text>
        <List
          dense
          disablePadding
          sx={{
            listStyleType: "disc",
            paddingLeft: 7,
            color: (theme) => theme.palette.text.secondary,
          }}
        >
          {[
            "you cancelled your shift",
            "you called in sick",
            "you were sent home for performance reasons",
            "you arrived late to your shift",
          ].map((item) => (
            <ListItem
              key={item}
              disableGutters
              disablePadding
              sx={{ display: "list-item", padding: 0, minHeight: 0 }}
            >
              <Text variant="body2" sx={{ color: (theme) => theme.palette.text.secondary }}>
                {item}
              </Text>
            </ListItem>
          ))}
        </List>
        <Text variant="body2" sx={{ color: (theme) => theme.palette.text.secondary }}>
          {/* TODO: Add link to full policy */}
          View the{" "}
          <TextButton
            size="medium"
            sx={(theme) => ({
              display: "inline",
              height: "auto",
              verticalAlign: "baseline",
              color: theme.palette.text.secondary,
            })}
          >
            full policy
          </TextButton>{" "}
          to learn more
        </Text>
      </TermsListItem>

      <TermsListItem iconType="coin" title="No extra steps needed">
        Your full earnings will be automatically deposited into your account. We&apos;ll notify you
        once it&apos;s done.
      </TermsListItem>

      {props.variant === "overview" && (
        <TermsListItem iconType="building" title="Payout may differ between workplaces">
          Protection is live at most workplaces. Look for &quot;Cancellation Protection&quot; in the
          listing for full terms.
        </TermsListItem>
      )}

      <TermsListItem iconType="calendar-remove" title="Your rate includes protection">
        Your rate may appear lower as a portion goes toward protecting your earnings
      </TermsListItem>
    </Stack>
  );
}
